import { useCallback, useEffect, useRef } from 'react';
import { useAuth } from '../zustand/Zustand';
import { Sentry } from '@/helpers/sentry';

const CHANNEL_NAME = 'flippingpal-subscription-sync';
const STORAGE_KEY = 'flippingpal-subscription-updated';

interface SyncMessage {
  type: 'SUBSCRIPTION_UPDATED' | 'USER_DATA_UPDATED';
  timestamp: number;
  userId?: string;
}

/**
 * Hook that syncs user data changes across browser tabs.
 * When a user upgrades/downgrades subscription or updates their profile in one tab,
 * all other tabs will refresh their user data.
 */
export const useSubscriptionSync = () => {
  const { userData, fetchUserData } = useAuth();
  const channelRef = useRef<BroadcastChannel | null>(null);
  const lastSyncRef = useRef<number>(0);

  // Handle incoming sync messages
  const handleSyncMessage = useCallback(
    async (message: SyncMessage) => {
      // Debounce: ignore if we just synced
      if (Date.now() - lastSyncRef.current < 1000) {
        return;
      }

      // Only sync if it's for the same user or no user specified
      if (message.userId && userData?.id && message.userId !== userData.id) {
        return;
      }

      lastSyncRef.current = Date.now();

      // Refresh user data from the server
      try {
        await fetchUserData();
      } catch (e) {
        Sentry.logger.warn('Failed to refresh user data after sync', { error: e });
      }
    },
    [userData, fetchUserData]
  );

  useEffect(() => {
    // Set up BroadcastChannel if supported
    if (typeof BroadcastChannel !== 'undefined') {
      channelRef.current = new BroadcastChannel(CHANNEL_NAME);
      channelRef.current.onmessage = (event: MessageEvent<SyncMessage>) => {
        if (event.data?.type === 'SUBSCRIPTION_UPDATED' || event.data?.type === 'USER_DATA_UPDATED') {
          handleSyncMessage(event.data);
        }
      };
    }

    // Set up localStorage listener as fallback
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === STORAGE_KEY && event.newValue) {
        try {
          const message: SyncMessage = JSON.parse(event.newValue);
          if (message.type === 'SUBSCRIPTION_UPDATED' || message.type === 'USER_DATA_UPDATED') {
            handleSyncMessage(message);
          }
        } catch {
          // Invalid JSON, ignore
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      if (channelRef.current) {
        channelRef.current.close();
        channelRef.current = null;
      }
    };
  }, [handleSyncMessage]);
};

/**
 * Call this function after a successful subscription change or user data update
 * (upgrade, downgrade, cancellation, profile update, etc.)
 */
export const broadcastUserDataChange = (userId?: string, type: 'SUBSCRIPTION_UPDATED' | 'USER_DATA_UPDATED' = 'SUBSCRIPTION_UPDATED') => {
  const message: SyncMessage = {
    type,
    timestamp: Date.now(),
    userId,
  };

  // BroadcastChannel
  if (typeof BroadcastChannel !== 'undefined') {
    const channel = new BroadcastChannel(CHANNEL_NAME);
    channel.postMessage(message);
    channel.close();
  }

  // localStorage fallback
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(message));
  } catch (e) {
    Sentry.logger.warn('Failed to broadcast user data change', { error: e });
  }
};

// Alias for backward compatibility
export const broadcastSubscriptionChange = broadcastUserDataChange;